import { SiteInfo } from '../../views/CalendarMultiView'
import { useSignalLayer } from '../../hooks/useSignalLayer'
import { OnboardingTourHandle } from '../OnboardingTour'
import { fmtDate } from '../../constants/gantt.logic'
import { Badge, Button } from '@indipia/ui-core'
import { TodayTodoButton } from '../SignalControls'
import { Z_CONTEXT_MENU } from '../../constants/zIndex'
import { useSchedulerHost } from '../../context/SchedulerHostContext'
import { workStatusWritePath } from '../../constants/signalView'
// 상단 정보줄(현장 정보 · 액션 버튼 · 드롭다운)
// selectedSite 가 없으면 현장 미선택 모드: 현장 의존 값·버튼은 숨기거나 비활성 처리

export type GanttInfoBarProps = {
  selectedSite?: SiteInfo | null
  endDt?: Date | null
  dDay?: string
  signal?: ReturnType<typeof useSignalLayer>
  tourRef?: React.RefObject<OnboardingTourHandle>
  handlePDF: () => void
  setOpenDd: React.Dispatch<React.SetStateAction<"dd1" | "dd2" | "dd3" | null>>
  openDd: "dd1" | "dd2" | "dd3" | null
  setShowSigongPanel?: React.Dispatch<React.SetStateAction<boolean>>
  openMaterialPopup?: (taskId: number | null) => void
  setDeepLinkTaskId?: React.Dispatch<React.SetStateAction<number | null>>
  setShowShipPanel?: React.Dispatch<React.SetStateAction<boolean>>
  setShowStatusPanel?: React.Dispatch<React.SetStateAction<boolean>>
  setShowAddPop?: React.Dispatch<React.SetStateAction<boolean>>
  setShowMilestonePop?: React.Dispatch<React.SetStateAction<boolean>>
}

export function GanttInfoBar({
  selectedSite, endDt, dDay, signal, tourRef, handlePDF, setOpenDd, openDd, setShowSigongPanel,
  openMaterialPopup, setDeepLinkTaskId, setShowShipPanel, setShowStatusPanel, setShowAddPop,
  setShowMilestonePop,
}: GanttInfoBarProps) {
  const disabled = !selectedSite
  // 호스트가 주입한 작업요청 작성 이동(F-04) — 없으면 [작업요청 작성] 항목을 숨긴다. 보고 있는 현장을 넘겨 작업요청 작성 화면의 기본 현장으로 쓴다
  const schedulerHost = useSchedulerHost()
  const ic = disabled ? 'var(--shell-hint)' : 'var(--shell-on-surface)'
  const icPrimary = disabled ? 'var(--shell-hint)' : 'var(--gt-primary)'
  // 메뉴 항목 속성: 미선택이면 눌러도 반응 없는 비활성 항목
  const itemProps = (run: () => void) => disabled
    ? { className: 'menu-item', 'aria-disabled': 'true' as const, style: { cursor: 'not-allowed', color: 'var(--shell-hint)', background: 'transparent' } }
    : {
        role: 'button', tabIndex: 0,
        onClick: (e: React.MouseEvent) => { e.stopPropagation(); run(); setOpenDd(null) },
        onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); run(); setOpenDd(null) } },
        className: 'menu-item',
      }
  return (
    <div style={{ background: 'var(--shell-surface)', padding: '0 18px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', alignItems: 'center', minHeight: 52, flexShrink: 0, gap: 16 }}>
      {/* 현장 정보 — 현장명(고객명) · 공사기간 · D-day 순으로 한 줄에 배치 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {selectedSite?.siteName || '—'}
          {selectedSite?.customerName && <span style={{ color: 'var(--shell-on-surface-2)', fontWeight: 600 }}>({selectedSite.customerName})</span>}
        </span>
        {selectedSite?.startDate && selectedSite.endDate && (
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '-.1px', flexShrink: 0 }}>
            {fmtDate(new Date(selectedSite.startDate))} ~ {fmtDate(new Date(selectedSite.endDate))}
          </span>
        )}
        {/* 종료일이 지난 현장(D+N)은 중립색(F-09) */}
        {endDt && dDay && <Badge status={dDay.startsWith('D+') ? 'done' : 'info'} className="shrink-0">{dDay}</Badge>}
      </div>
      {/* 액션 버튼 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {!disabled && signal && (
          <TodayTodoButton
            count={signal.todoCount}
            isError={signal.todosQ.isError}
            isLoading={signal.todosQ.isLoading}
            onOpen={anchor => signal.setPanel({ anchor })}
          />
        )}
        {tourRef && <button onClick={() => tourRef.current?.start()} title="화면 가이드 다시 보기" aria-label="화면 가이드 다시 보기" style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', border: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--shell-on-surface-2)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>?</button>}
        {/* 현장 미선택에서도 눌러 안내 토스트를 받도록 disabled 대신 aria-disabled(F-16) */}
        <button onClick={handlePDF} aria-disabled={disabled || undefined} title="인쇄 · PDF 저장" aria-label="인쇄 · PDF 저장" style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', border: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)', cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: disabled ? 'var(--shell-hint)' : 'var(--shell-on-surface-2)' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        </button>
        {/* 파트너배정 / 알림발송 */}
        <div style={{ position: 'relative' }}>
          <Button
            variant="secondary"
            size="md"
            className="gap-1"
            onClick={e => { e.stopPropagation(); setOpenDd(openDd === 'dd2' ? null : 'dd2') }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            파트너배정 / 알림발송<span style={{ fontSize: 'var(--type-caption-size)', marginLeft: 2 }}>▼</span>
          </Button>
          {openDd === 'dd2' && (
            <div className="menu show" style={{ left: 'auto', right: 0, width: 'max-content', minWidth: 200, whiteSpace: 'nowrap', zIndex: Z_CONTEXT_MENU }}>
              <div data-tour="menu-sigong" {...itemProps(() => setShowSigongPanel?.(true))}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                시공파트너배정-&gt;일정확인발송
              </div>
              <div data-tour="menu-material" {...itemProps(() => openMaterialPopup?.(null))}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                자재파트너배정-&gt;발주정보발송
              </div>
              <div {...itemProps(() => { setDeepLinkTaskId?.(null); setShowShipPanel?.(true) })}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                배송정보현황-&gt;요청
              </div>
            </div>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <Button
            variant="secondary"
            size="md"
            className="gap-1"
            onClick={e => { e.stopPropagation(); setOpenDd(openDd === 'dd3' ? null : 'dd3') }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            작업요청/현황<span style={{ fontSize: 'var(--type-caption-size)', marginLeft: 2 }}>▼</span>
          </Button>
          {openDd === 'dd3' && (
            <div className="menu show" style={{ left: 'auto', right: 0, minWidth: 150, zIndex: Z_CONTEXT_MENU }}>
              {schedulerHost?.openWorkOrder && (
                <div {...itemProps(() => schedulerHost.openWorkOrder?.({ siteId: selectedSite?.siteId }))}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
                  작업요청 작성
                </div>
              )}
              {/* 작업현황(작업보고) 작성 — 보고 있는 스케줄을 넘겨 작성 화면이 그 현장을 미리 고른다. 호스트 이동이 없으면 숨김 */}
              {schedulerHost?.openPath && (
                <div {...itemProps(() => schedulerHost.openPath?.(workStatusWritePath(selectedSite?.scheduleId)))}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
                  작업현황 작성
                </div>
              )}
              <div {...itemProps(() => setShowStatusPanel?.(true))}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                공사현황 보기
              </div>
            </div>
          )}
        </div>
        {/* 공종 추가 */}
        <div data-tour="add-btn" style={{ position: 'relative' }}>
          <Button
            variant="primary"
            size="md"
            className="gap-1"
            onClick={e => { e.stopPropagation(); setOpenDd(openDd === 'dd1' ? null : 'dd1') }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><line x1="5" y1="1" x2="5" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="5" x2="9" y2="5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            공종 추가<span style={{ fontSize: 'var(--type-caption-size)', marginLeft: 2, opacity: 0.8 }}>▼</span>
          </Button>
          {openDd === 'dd1' && (
            <div className="menu show" style={{ left: 'auto', right: 0, minWidth: 150, zIndex: Z_CONTEXT_MENU }}>
              <div {...itemProps(() => setShowAddPop?.(true))}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><line x1="5" y1="1" x2="5" y2="9" stroke={icPrimary} strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="5" x2="9" y2="5" stroke={icPrimary} strokeWidth="1.8" strokeLinecap="round"/></svg>
                공종 추가
              </div>
              <div {...itemProps(() => setShowMilestonePop?.(true))}>
                <svg width="10" height="10" viewBox="0 0 20 20"><polygon points="10,1 19,10 10,19 1,10" fill={icPrimary}/></svg>
                마일스톤
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
